import React, { useCallback } from 'react';

import {
  Box,
  TableRow,
  TableCell,
  Text,
  DropdownOverlay,
  ActionList,
  ActionListItem,
  SelectInput,
  Amount,
  TableEditableCell,
  TableEditableDropdownCell,
  Tooltip,
} from '@razorpay/blade/components';

import { AssignedFlexibleComponentsTableDataType } from '../../types';
import { YearlyAmountColumn } from './YearlyAmountColumn';
import { isFBPUnitAmount } from 'components/PayDetails/utils';
import { DECIMAL_AND_FOLLOWING } from 'utils/regexes';

const MonthlyDeclaration = ({
  rowData,
  onChange,
  value,
  monthlyDeclarationErrors,
  isAdmin,
}: {
  rowData: AssignedFlexibleComponentsTableDataType;
  value: string;
  onChange: ({ id, value }: { id: string; value: string }) => void;
  monthlyDeclarationErrors: Record<string, string>;
  isAdmin: boolean;
}) => {
  const handleFixedChange = useCallback(
    (newValue?: string) => {
      const isAmount = isFBPUnitAmount(rowData.unit);
      const sanitizedValue = isAmount && newValue ? newValue.replace(DECIMAL_AND_FOLLOWING, '') : newValue;
      onChange({ id: rowData.id, value: sanitizedValue || '' });
    },
    [rowData.id, rowData.unit, onChange],
  );

  const handleDropdownChange = useCallback(
    ({ values }: { values: string[] }) => {
      onChange({ id: rowData.id, value: values[0] });
    },
    [rowData.id, onChange],
  );

  if (rowData.monthlyDeclaration.type === 'FIXED' || (!isAdmin && !rowData.canDeclare)) {
    const isAmount = isFBPUnitAmount(rowData.unit);
    return (
      <Box width={'100%'}>
        <TableCell>
          <Tooltip
            content="Value cannot be modified for Fixed or Fully Allocated components."
            placement="top">
            <Text>
              {isAmount ? (
                <Amount value={Number(value)} />
              ) : (
                `${value}${rowData.monthlyDeclaration.suffix}`
              )}
            </Text>
          </Tooltip>
        </TableCell>
      </Box>
    );
  } else if (rowData.monthlyDeclaration.type === 'LIST_OF_OPTION') {
    return (
      <TableEditableDropdownCell>
        <SelectInput
          value={value}
          label=""
          placeholder="Select amount"
          name="select monthly declaration"
          onChange={handleDropdownChange}
        />
        <DropdownOverlay>
          <ActionList>
            {rowData.monthlyDeclaration?.options.map((item) => (
              <ActionListItem key={item.label} value={item.value} title={item.label} />
            ))}
          </ActionList>
        </DropdownOverlay>
      </TableEditableDropdownCell>
    );
  }

  return (
    <TableEditableCell
      accessibilityLabel={`${rowData.component_name}_monthly_declaration`}
      onChange={({ value }) => handleFixedChange(value)}
      value={value}
      placeholder="Enter declaration per month"
      errorText={monthlyDeclarationErrors[rowData.id]}
      validationState={!!monthlyDeclarationErrors[rowData.id] ? 'error' : 'none'}
      suffix={rowData.monthlyDeclaration.suffix ?? ''}
    />
  );
};

const MaxMonthlyAmount = ({ rowData }: { rowData: AssignedFlexibleComponentsTableDataType }) => {
  if (!rowData.maxValuePerMonth) return <>--</>;
  const isAmount = isFBPUnitAmount(rowData.unit);
  return (
    <>
      <Text>
        {isAmount ? (
          <Amount value={Number(rowData.maxValuePerMonth)} />
        ) : (
          `${rowData.maxValuePerMonth}${rowData.monthlyDeclaration.suffix}`
        )}
      </Text>
    </>
  );
};

export const AllocateFBPTableRow = ({
  rowData,
  monthlyDeclarationValue,
  onChange,
  monthlyDeclarationErrors,
  isAdmin,
}: {
  rowData: AssignedFlexibleComponentsTableDataType;
  monthlyDeclarationValue: string;
  onChange: ({ id, value }: { id: string; value: string }) => void;
  monthlyDeclarationErrors: Record<string, string>;
  isAdmin: boolean;
}) => {
  return (
    <TableRow
      item={rowData}
      key={rowData.id}
      testID={`flexible-benefit-component-row-${rowData.component_name}`}>
      <TableCell>
        <Text size="medium" weight="regular">
          {isAdmin ? rowData.component_name : rowData.display_name}
        </Text>
      </TableCell>
      <TableCell>
        <Text size="medium" weight="regular">
          {rowData.taxableType}
        </Text>
      </TableCell>
      <TableCell textAlign="right">
        <MaxMonthlyAmount rowData={rowData} />
      </TableCell>

      <MonthlyDeclaration
        value={monthlyDeclarationValue}
        rowData={rowData}
        onChange={onChange}
        monthlyDeclarationErrors={monthlyDeclarationErrors}
        isAdmin={isAdmin}
      />

      <TableCell textAlign="right">
        <YearlyAmountColumn rowData={rowData} monthlyDeclarationValue={monthlyDeclarationValue} />
      </TableCell>
    </TableRow>
  );
};
